// TYPES
import * as THREE from 'three';
import { CustomEase } from 'gsap/CustomEase';
// NOTE: KEEP PointData, setupDataKeys AND pointDataKeys IN SYNC WITH THE EDITOR
export const fontColorHex = '#2D3946';
export const fontColor = new THREE.Color(fontColorHex);
export const setupDataKeys = [
    'iteration',
    'num_of_lines',
    'opacity',
    'r_opacity',
    'speed',
    'r_speed',
    'r_x',
    'r_y',
    'r_width',
    'rotation_iteration',
    'rotation_x',
    'rotation_y',
    'rotation_z',
];
export const pointDataKeys = ['x', 'y', 'z', 'color', 'width', 'opacity'];
const parsePointData = (keys, data) => keys.reduce((acc, key, idx) => {
    const d = parseFloat(data[idx]);
    return {
        ...acc,
        [key]: Number.isNaN(d) ? 0 : d,
    };
}, {});
export var AnimScene;
(function (AnimScene) {
    AnimScene["None"] = "none";
    AnimScene["Circles"] = "circles";
    AnimScene["Meta"] = "meta";
    AnimScene["Sphere"] = "sphere";
    AnimScene["Logo_0"] = "logo_0";
    AnimScene["Logo_1"] = "logo_1";
    AnimScene["Logo_2"] = "logo_2";
    AnimScene["Logo_3"] = "logo_3";
    AnimScene["Logo_4"] = "logo_4";
    AnimScene["Logo_5"] = "logo_5";
    AnimScene["ProteinPoints"] = "protein";
})(AnimScene || (AnimScene = {}));
export var EffectType;
(function (EffectType) {
    EffectType["Trail"] = "trail";
    EffectType["Meta"] = "meta";
})(EffectType || (EffectType = {}));
//
// CONSTS
//
export const proteinEvent = 'protein';
const particleSetup = [
    { color: '#69FF43', color2: '#dbceff' },
    { color: '#A001FE', color2: '#ffe3c3' },
    { color: '#FE0180', color2: '#ffd0d1' },
    { color: '#FF3402', color2: '#c4ddfc' },
    { color: '#FEE51C', color2: '#f6fedc' },
    { color: '#05A6FF', color2: '#cbfff9' },
];
export const renderOrder = [
    AnimScene.Circles,
    AnimScene.ProteinPoints,
    AnimScene.Logo_0,
    AnimScene.Logo_1,
    AnimScene.Logo_2,
    AnimScene.Logo_3,
    AnimScene.Logo_4,
    AnimScene.Logo_5,
    AnimScene.None,
];
export const animSceneEffects = {
    [AnimScene.Meta]: [EffectType.Meta],
};
// update vertexCount in baseVertexShader when you change VERTEX_COUNT;
export const VERTEX_COUNT = 9604;
export const TEXELS_PER_VERTEX = 4; // 4 pixels (4*4=16 floats per vertex)
const totalTexels = VERTEX_COUNT * TEXELS_PER_VERTEX;
export const texSize = Math.ceil(Math.sqrt(totalTexels));
export const mapSize = Math.ceil(Math.sqrt(VERTEX_COUNT));
export const scaleToHeight = 0.86;
const baseScale = 1;
export const cameraZ = 1600;
const layerNum = 60;
const layerDistance = 6.52;
const metaDistance = 80;
const maxSpeed = 0.3;
const minSpeed = 0.06;
const speedUpAmount = 2.2;
const maxSpeedMeta = 0.1;
const minSpeedMeta = 0.04;
const minLerpSpeed = 0.06;
const maxLerpSpeed = 0.08;
export const randomAnimationDelayTime = 3.8;
export const randomAnimationSwitchTime = 1.3;
export const randomAnimationSwitchEase = 'power3.inOut';
export const proteinPoints = new Array(6).fill(null);
export const proteinPointDistance = 128;
export const logoCssDistance = 784;
export const baseViewHeight = 1140; // Desired view height (when not width-limited)
export const ratioThreshold = 0.8;
export const animSceneDuration = 1.9;
//export const animSceneEaseEnter = CustomEase.create("custom", "M0,0 C0.312,0.24 0,1 1,1 ");
//export const animSceneEaseEnter = CustomEase.create("custom", "M0,0 C0.138,0.069 0.088,0.62 0.335,0.84 0.544,1.025 0.665,1 1,1 ");
// export const animSceneEaseEnter = CustomEase.create("custom", "M0,0 C0.138,0.069 0.066,0.662 0.323,0.869 0.499,1.011 0.665,1 1,1 ");
// export const animSceneEaseEnter = CustomEase.create("custom", "M0,0 C0.214,0.068 0.13,0.567 0.342,0.821 0.475,0.981 0.665,1 1,1 ");
export const animSceneEaseEnter = CustomEase.create('custom', 'M0,0 C0.198,0.095 0.097,0.536 0.3,0.8 0.452,1 0.665,1 1,1 ');
export const animSceneEaseRapid = CustomEase.create('custom', 'M0,0 C0,0.843 0.42,1 1,1 ');
//export const animSceneEaseEnter = CustomEase.create("custom", "M0,0 C0.198,0.095 0.09,0.541 0.3,0.8 0.452,0.987 0.665,1 1,1 ");
//export const animSceneEaseRotateIn = CustomEase.create("custom", "M0,0 C0.038,0.102 0.232,1 1,1 ");
export const animSceneEaseRotateIn = CustomEase.create('custom', 'M0,0 C0.258,0.061 0.379,1 1,1 ');
export const animSceneEaseRotateOut = CustomEase.create('custom', 'M0,0 C0.396,0 0.379,1 1,1 ');
export const animSceneEaseExit = 'power4.in';
export const lerp = (x, y, a) => x * (1 - a) + y * a;
export const colorNumToString = (color) => `#${color.toString(16).padStart(6, '0')}`;
export const getInitialVertices = () => {
    const radius = layerNum * layerDistance + layerDistance * 6;
    const vertices = new Float32Array(VERTEX_COUNT * 3);
    for (var i = 0; i < VERTEX_COUNT; i++) {
        const point = getRandomPointOnArc(radius, 0, 2 * Math.PI);
        vertices[i * 3] = point.x;
        vertices[i * 3 + 1] = point.y;
        vertices[i * 3 + 2] = 0;
    }
    return vertices;
};
export const getVertexIds = () => {
    const vertices = new Float32Array(VERTEX_COUNT);
    for (var i = 0; i < VERTEX_COUNT; i++) {
        vertices[i] = i;
    }
    return vertices;
};
const distributeParticles = (VERTEX_COUNT, layerNum, ratio) => {
    if (layerNum === 1)
        return [VERTEX_COUNT];
    const r = Math.pow(1 / ratio, 1 / (layerNum - 1));
    const totalFactor = Math.pow(r, layerNum) - 1;
    const A = (VERTEX_COUNT * (r - 1)) / totalFactor;
    const layers = [];
    let sum = 0;
    for (let i = 0; i < layerNum; i++) {
        const ideal = A * Math.pow(r, i);
        const rounded = Math.round(ideal);
        layers.push(rounded);
        sum += rounded;
    }
    // Adjust any rounding error by adding/subtracting the difference to the last element.
    const diff = VERTEX_COUNT - sum;
    layers[layerNum - 1] += diff;
    return layers;
};
//
// CIRCLES
//
/**
 * Generates a random point on a circular arc.
 * @param radius - The radius of the circle.
 * @param startAngle - The start angle of the arc (in radians).
 * @param endAngle - The end angle of the arc (in radians).
 * @returns A point {x: number, y: number} on the arc.
 */
export const getRandomPointOnArc = (radius, startAngle, endAngle) => {
    const randomAngle = startAngle + Math.random() * (endAngle - startAngle);
    const x = radius * Math.cos(randomAngle);
    const y = radius * Math.sin(randomAngle);
    if (isNaN(x) || isNaN(y)) {
        return {
            x: 0,
            y: 0,
        };
    }
    return { x, y };
};
/**
 * Rotates a point around a given center in 2D space.
 * @param point - The point to rotate, as {x: number, y: number}.
 * @param center - The center of rotation, as {x: number, y: number}.
 * @param angle - The angle of rotation in radians (counterclockwise).
 * @returns The rotated point as {x: number, y: number}.
 */
export const rotateAroundPoint = (point, center, angle) => {
    const translatedX = point.x - center.x;
    const translatedY = point.y - center.y;
    const rotatedX = translatedX * Math.cos(angle) - translatedY * Math.sin(angle);
    const rotatedY = translatedX * Math.sin(angle) + translatedY * Math.cos(angle);
    return {
        x: rotatedX + center.x,
        y: rotatedY + center.y,
    };
};
export const getPointOnSphere = (radius, index, // current vertex index (from 0 to allVertices - 1)
textureWidth, // virtual texture “width” (number of columns)
textureHeight) => {
    const col = index % textureWidth;
    const row = Math.floor(index / textureWidth);
    const u = (col + 0.5) / textureWidth;
    const v = (row + 0.5) / textureHeight;
    const theta = u * 2 * Math.PI;
    const phi = Math.acos(1 - 2 * v);
    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.sin(theta);
    return [x, y, z];
};
export const zShift = (x0, y0, z0, z1, cameraDepth) => {
    const cameraDistance = Math.abs(cameraDepth);
    const scale = (cameraDistance - z1) / (cameraDistance - z0);
    const x1 = x0 * scale;
    const y1 = y0 * scale;
    return { x: x1, y: y1, z: z1 };
};
const getVerticesCircle = () => {
    const vertices = [];
    const concentricSetup = new Array(layerNum).fill(null).map(() => {
        const particle = particleSetup[Math.floor(Math.random() * particleSetup.length)];
        return particle;
    });
    let count = 0;
    const distributedCounts = distributeParticles(VERTEX_COUNT, layerNum, 0.23);
    for (let l = 0; l < distributedCounts.length; l++) {
        let speedMultiplier = (1 - l / distributedCounts.length) * 2 + 1;
        if (l < 3) {
            speedMultiplier *= 4;
        }
        for (let i = 0; i < distributedCounts[l]; i++) {
            const setup = {};
            const point = getRandomPointOnArc(l * layerDistance + layerDistance * 4, 0, 2 * Math.PI);
            const { x, y, z } = zShift(point.x, point.y, 0, Math.random() * -1000 + 500, cameraZ);
            setup.position = [x, y, z];
            const { r, g, b } = new THREE.Color(concentricSetup[l].color);
            setup.color = [r, g, b];
            const getSpeed = () => Math.random() < 0.1
                ? Math.random() * maxSpeed - maxSpeed / 2
                : Math.random() * minSpeed - minSpeed / 2;
            setup.speed = [0, 0, getSpeed() * speedMultiplier];
            setup.center = [0, 0, 0];
            setup.opacity = Math.random() < 0.2 ? 1 : Math.random();
            setup.scale = baseScale;
            vertices.push(setup);
            count++;
        }
    }
    return vertices;
};
/*
const getVerticesMeta = (): VertexSetup[] => {
  const vertices: VertexSetup[] = [];

  let count = 0;
  const randomPos = (): number => Math.random() * -60 + 30;
  const randomTurn = (): number => Math.random() / 6;

  const getSpeed = (): number =>
    Math.random() < 0.1
      ? Math.random() * maxSpeedMeta - maxSpeedMeta
      : Math.random() * minSpeedMeta - minSpeedMeta;

  const distributedCounts = distributeParticles(
    VERTEX_COUNT,
    particleSetup.length,
    0.1,
  );

  for (let l = 0; l < distributedCounts.length; l++) {
    for (let i = 0; i < distributedCounts[l]; i++) {
      const setup: Partial<VertexSetup> = {};
      const groupCount = l * 3;
      const step = Math.floor(2 * Math.PI) / groupCount;
      //const startTurn = (l * Math.PI) / 2;

      //const angle = Math.floor((i / distributedCounts[l]) * groupCount) * step;

      const centerPoint = getRandomPointOnArc(l * metaDistance, 0, 2 * Math.PI);

      setup.center = [centerPoint.x, centerPoint.y, Math.random() * 200];

      const point: [number, number, number] = [
        setup.center[0] + randomPos(),
        setup.center[1] + randomPos(),
        0,
      ];

      setup.position = [...point];

      setup.center = [0, 0, 0];

      const { r, g, b } = new THREE.Color(particleSetup[l].color);

      setup.color = [r, g, b];

      setup.speed = [0, 0, getSpeed() * (Math.random() < 0.5 ? 1 : -1)];

      setup.opacity = i % 5 ? 0 : 1;
      setup.scale = !setup.opacity
        ? 0
        : Math.random() < 0.1 && setup.opacity
          ? Math.random() * 0.15 * baseScale
          : (Math.random() * 0.05 + 0.01) * baseScale;

      vertices.push(setup as VertexSetup);
      count++;
    }
  }
  return vertices;
};

 const sphereSpeed = 0.3;

const getVerticesSphere = (): VertexSetup[] => {
  const vertices: VertexSetup[] = [];

  let count = 0;

  for (let i = 0; i < VERTEX_COUNT; i++) {
    const setup: Partial<VertexSetup> = {};

    const setupNum = Math.floor((i / VERTEX_COUNT) * particleSetup.length);

    setup.center = [0, 0, 0];

    setup.position = getPointOnSphere(400, i, mapSize, mapSize);

    const { r, g, b } = new THREE.Color(particleSetup[setupNum].color);

    setup.color = [r, g, b];

   
    setup.speed = [0, sphereSpeed, sphereSpeed];
    //setup.speed = [0,0,0];

    setup.opacity = 1;
    setup.scale = baseScale * 0.56;

    vertices.push(setup as VertexSetup);
    count++;
  }
  return vertices;
}; */
const logoScale = 100;
// The first elements of the logo setup are determining the assembly of the logo:
// NOTE! The first vertex contains the setup information
// Keep the length of the demoPointData synced with Editor
const demoPointData = new Array(9).fill(null);
export const getLogoCurve = (logoCurve) => {
    if (!logoCurve)
        return { vertices: [], setup: {} };
    const inputData = logoCurve.split(',');
    const setupData = {
        ...parsePointData(setupDataKeys, inputData.slice(0, setupDataKeys.length - 1)),
    };
    const pointsData = new Array(demoPointData.length - 1)
        .fill(null)
        .map((_, idx) => {
        const pointStart = setupDataKeys.length + pointDataKeys.length * idx;
        return parsePointData(pointDataKeys, inputData.slice(pointStart, pointStart + pointDataKeys.length));
    });
    const vertices = [];
    const curveDivisions = VERTEX_COUNT;
    const pts = pointsData.map((d) => {
        return new THREE.Vector3(d.x * logoScale, d.y * logoScale, d.z * logoScale);
    });
    const colors = pointsData.map((d) => {
        const colorString = colorNumToString(d.color);
        return colorString ?? '#FFFFFF';
    });
    // Create the curve with chordal type
    const curve = new THREE.CatmullRomCurve3(pts, false);
    curve.curveType = 'chordal'; //  "centripetal" | "chordal" | "catmullrom"
    curve.tension = 0.8;
    // Sample the curve
    const points = curve.getPoints(curveDivisions);
    for (let i = 0; i < points.length - 1; i++) {
        const point = points[i];
        const setup = {};
        setup.position = [point.x, point.y, point.z];
        const t = i / curveDivisions;
        const segCount = pointsData.length;
        const segment = Math.floor(t * segCount);
        const tSegment = (t - segment / segCount) * segCount;
        const c1 = new THREE.Color(colors[segment]);
        const c2 = new THREE.Color(colors[Math.min(segment + 1, segCount - 1)]);
        // Interpolate between the two breakpoint colors
        const color = c1.clone().lerp(c2, tSegment);
        setup.color = [color.r, color.g, color.b];
        const scale1 = pointsData[segment].width;
        const scale2 = pointsData[Math.min(segment + 1, pointsData.length - 1)].width;
        const scale = lerp(scale1, scale2, tSegment);
        setup.scale = scale * baseScale;
        const opacity1 = pointsData[segment].opacity;
        const opacity2 = pointsData[Math.min(segment + 1, pointsData.length - 1)].opacity;
        const opacity = lerp(opacity1, opacity2, tSegment);
        setup.opacity = opacity;
        // TODO fill with more random values
        setup.center = [0, 0, 0];
        setup.speed = [0, 0, 0];
        vertices.push(setup);
    }
    return { vertices, setup: setupData };
};
const randomMap = {
    position: 0.1,
    scale: 1,
    opacity: 1,
    speed: 1,
};
const getRandomValue = (random, randomMap, fixed) => {
    if (!random)
        return 0;
    if (fixed) {
        return Math.random() * random * randomMap;
    }
    return Math.random() * random * randomMap * 2 - randomMap;
};
export const getVerticesLogo = (logoCurve, speedUp) => {
    if (!logoCurve)
        return [];
    const inputData = logoCurve.split(',');
    const setupData = {
        ...parsePointData(setupDataKeys, inputData.slice(0, setupDataKeys.length - 1)),
    };
    /* const pointsData: PointData[] = new Array(demoPointData.length - 1)
      .fill(null)
      .map((_, idx) => {
        const pointStart = setupDataKeys.length + pointDataKeys.length * idx;
        return parsePointData(
          pointDataKeys,
          inputData.slice(pointStart, pointStart + pointDataKeys.length),
        );
      });
   */
    const vertices = [];
    const iteration = setupData.iteration ?? 0;
    const lineCount = setupData.num_of_lines ?? 0;
    const scaleRandom = setupData.r_width ?? 0;
    const opacity = setupData.opacity ?? 1;
    const opacityRandom = setupData.r_opacity ?? 0;
    const speed = setupData.speed ?? 0;
    const speedRandom = setupData.r_speed ?? 0;
    //TODO: xRandom is not in use;
    const xRandom = setupData.r_x ?? 0;
    const yRandom = setupData.r_y ?? 0;
    const zRandom = setupData.r_z ?? 0;
    for (let i = 0; i < iteration * lineCount; i++) {
        const s = speed + Math.round(getRandomValue(speedRandom, randomMap.speed));
        const setup = {
            position: [
                getRandomValue(xRandom / 100, VERTEX_COUNT, true),
                // random applied to the rotation
                getRandomValue(yRandom, randomMap.position),
                // random applied to position on curve
                0,
            ],
            scale: 1 + getRandomValue(scaleRandom, randomMap.scale, true),
            opacity: opacity - getRandomValue(opacityRandom, randomMap.opacity, true),
            speed: [speedUp ? s * speedUpAmount : s, 0, 0],
            center: [0, 0, 0],
            color: [0, 0, 0],
        };
        vertices.push(setup);
    }
    return fillVertexCount(vertices);
};
export const fillVertexCount = (vertices) => {
    return new Array(VERTEX_COUNT)
        .fill(null)
        .map((_, idx) => vertices[idx] ?? { ...defaultVertex });
};
export const vertexGenerators = {
    [AnimScene.Circles]: getVerticesCircle,
    [AnimScene.Logo_0]: getVerticesLogo,
    [AnimScene.Logo_1]: getVerticesLogo,
    [AnimScene.Logo_2]: getVerticesLogo,
    [AnimScene.Logo_3]: getVerticesLogo,
    [AnimScene.Logo_4]: getVerticesLogo,
    [AnimScene.Logo_5]: getVerticesLogo,
};
const defaultVertex = {
    scale: 0,
    opacity: 0,
    speed: [0, 0, 0],
    color: [1, 1, 1],
    center: [0, 0, 0],
    position: [0, 0, 0],
};
export const getDataTexture = (vertexArray) => {
    const data = new Float32Array(texSize * texSize * 4);
    for (let i = 0; i < VERTEX_COUNT; i++) {
        const { position, scale, speed, center, color, opacity } = vertexArray[i] ?? defaultVertex;
        const offset = i * TEXELS_PER_VERTEX * 4; // 16 floats allocated per vertex
        // Pixel 1 (RGBA): circles_scale, circles_opacity, circles_speed.x, circles_speed.y
        data[offset + 0] = scale;
        data[offset + 1] = opacity;
        data[offset + 2] = speed[0];
        data[offset + 3] = speed[1];
        // Pixel 2 (RGBA): speed.z, color.r, color.g, color.b
        data[offset + 4] = speed[2];
        data[offset + 5] = color[0];
        data[offset + 6] = color[1];
        data[offset + 7] = color[2];
        // Pixel 3 (RGBA): center.x, center.y, center.z, (unused or extra)
        data[offset + 8] = center[0];
        data[offset + 9] = center[1];
        data[offset + 10] = center[2];
        data[offset + 11] = 0.0; // unused
        // Pixel 4 (RGBA): position.x, position.y, position.z, (unused)
        data[offset + 12] = position[0];
        data[offset + 13] = position[1];
        data[offset + 14] = position[2];
        data[offset + 15] = 0.0; // unused
    }
    const texture = new THREE.DataTexture(data, texSize, texSize, THREE.RGBAFormat, THREE.FloatType);
    texture.needsUpdate = true;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.generateMipmaps = false;
    return texture;
};
// TODO: remove
export const metaShader = {
    uniforms: {
        tDiffuse: { value: null }, // This will be set automatically by ShaderPass.
        effect_amount: { value: 1.0 },
    },
    vertexShader: `
    varying vec2 vUv;
    void main(){
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
    fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float effect_amount;
    varying vec2 vUv;
    
    void main(){
      vec4 diffuse = texture2D(tDiffuse, vUv);
      
      // Compute the gooey effect.
      float newAlpha = clamp(diffuse.a * 80.0 - 10.0, 0.0, 1.0);
      vec3 pureColor = diffuse.a > 0.0 ? diffuse.rgb / diffuse.a : diffuse.rgb;
      float boostFactor = 1.0;
      pureColor *= boostFactor;
      vec3 effectColor = pureColor * newAlpha;
      
      // Blend between the original color and the effect.
      vec4 finalColor = mix(diffuse, vec4(effectColor, newAlpha), effect_amount);
      
      gl_FragColor = finalColor;
    }
  `,
};
/**
 * Dispose of all objects, geometries, and materials in a scene.
 */
export const disposeScene = (scene) => {
    scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
            // Dispose geometry
            object.geometry.dispose();
            // Dispose material and shaders
            if (object.material) {
                if (Array.isArray(object.material)) {
                    object.material.forEach((mat) => disposeMaterial(mat));
                }
                else {
                    disposeMaterial(object.material);
                }
            }
        }
    });
    // Remove all children from the scene
    while (scene.children.length > 0) {
        scene.remove(scene.children[0]);
    }
};
/**
 * Dispose of a material, including its shaders and textures.
 */
export const disposeMaterial = (material) => {
    if (!material)
        return;
    if (material instanceof THREE.ShaderMaterial ||
        material instanceof THREE.RawShaderMaterial) {
        // Dispose uniforms that are textures
        for (const key in material.uniforms) {
            if (material.uniforms[key].value instanceof THREE.Texture) {
                material.uniforms[key].value.dispose();
            }
        }
    }
    // Dispose material itself
    material.dispose();
};
/**
 * Dispose of all textures in the scene.
 */
export const disposeTextures = (scene) => {
    scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
            const material = object.material;
            if (Array.isArray(material)) {
                material.forEach((mat) => disposeMaterialTextures(mat));
            }
            else {
                disposeMaterialTextures(material);
            }
        }
    });
};
/**
 * Dispose of textures within a material.
 */
export const disposeMaterialTextures = (material) => {
    if (!material)
        return;
    for (const key in material) {
        if (Object.prototype.hasOwnProperty.call(material, key)) {
            const value = material[key];
            if (value instanceof THREE.Texture) {
                value.dispose();
            }
        }
    }
};
/**
 * Dispose of all buffers used in the scene.
 */
export const disposeBuffers = (scene) => {
    scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
            if (object.geometry instanceof THREE.BufferGeometry) {
                object.geometry.dispose();
            }
        }
    });
};
/**
 * Dispose of EffectComposer and all its passes.
 */
export const disposeEffectComposer = (composer) => {
    if (!composer)
        return;
    composer.passes.forEach((pass) => {
        if (pass.dispose) {
            pass.dispose();
        }
    });
    composer.passes = []; // Clear references
};
/**
 * Dispose of the WebGL renderer and lose the WebGL context.
 */
export const disposeRenderer = (renderer) => {
    if (renderer) {
        renderer.dispose();
        const gl = renderer.getContext();
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) {
            ext.loseContext();
        }
    }
};
/**
 * Full cleanup of Three.js scene, EffectComposer, and WebGL renderer.
 */
export const cleanUpThree = (scene, renderer, composer) => {
    if (scene) {
        disposeScene(scene);
        disposeTextures(scene);
        disposeBuffers(scene);
    }
    if (composer) {
        disposeEffectComposer(composer);
    }
    if (renderer) {
        disposeRenderer(renderer);
    }
};
export const asyncTimeout = (wait = 0) => {
    return new Promise((resolve) => {
        setTimeout(resolve, wait);
    });
};
